import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Shield, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_ACCOUNTS = [
  { label: 'Member', email: 'test@pivottopower.com', password: 'TestPassword123', icon: User, desc: 'Full member access' },
  { label: 'New Member', email: 'demo@pivottopower.com', password: 'DemoPassword123', icon: Zap, desc: 'Onboarding flow' },
  { label: 'Admin', email: 'admin@pivottopower.com', password: 'AdminPassword123', icon: Shield, desc: 'Admin dashboard' },
];

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect to dashboard
  const token = localStorage.getItem('pivot_token');
  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  const doLogin = async (email, password) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem('pivot_token', response.data.token);
      localStorage.setItem('pivot_user', JSON.stringify(response.data));
      toast.success('Welcome back!');
      if (response.data.onboarding_completed) {
        navigate('/dashboard');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      toast.error('Invalid credentials');
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    doLogin(formData.email, formData.password);
  };

  const handleQuickLogin = (account) => {
    setFormData({ email: account.email, password: account.password });
    doLogin(account.email, account.password);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black flex items-center justify-center py-12 px-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img src="https://customer-assets.emergentagent.com/job_clarity-network-1/artifacts/b1jdxi6j_image.png" alt="Pivot To Power" className="w-12 h-12 rounded-full" />
            <span className="text-3xl font-cormorant font-bold text-white">Pivot To Power</span>
          </div>
          <h1 className="text-4xl font-cormorant font-bold bg-gradient-to-r from-amber-300 to-pink-300 bg-clip-text text-transparent mb-2">Welcome Back</h1>
          <p className="text-gray-400 font-inter">Login to access your network</p>
        </div>

        {/* Quick Login Buttons */}
        <div className="mb-6">
          <p className="text-gray-500 text-xs font-inter uppercase tracking-wider mb-3 text-center">Quick Login</p>
          <div className="grid grid-cols-3 gap-2">
            {QUICK_ACCOUNTS.map((account) => {
              const Icon = account.icon;
              return (
                <button
                  key={account.label}
                  onClick={() => handleQuickLogin(account)}
                  disabled={loading}
                  className="bg-white/5 hover:bg-white/10 border border-white/15 hover:border-purple-400/50 rounded-xl p-3 text-center transition-all group disabled:opacity-50"
                  data-testid={`quick-login-${account.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-5 h-5 text-gray-400 group-hover:text-purple-400 mx-auto mb-1 transition-colors" />
                  <div className="text-white text-xs font-inter font-medium">{account.label}</div>
                  <div className="text-gray-500 text-[10px] font-inter">{account.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center space-x-3 mb-6">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-gray-500 text-xs font-inter">or enter credentials</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={handleLogin} className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20">
          <div className="space-y-5">
            <div>
              <label className="block text-gray-300 mb-2 font-inter font-medium">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-pink-500 focus:ring-2 focus:ring-pink-500/50 outline-none transition-all font-inter"
                placeholder="your@email.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-2 font-inter font-medium">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                className="w-full bg-black/30 border border-white/20 rounded-lg px-4 py-3 text-white focus:border-pink-500 focus:ring-2 focus:ring-pink-500/50 outline-none transition-all font-inter"
                placeholder="Enter your password"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-inter font-bold py-4 rounded-lg transition-all mt-6 shadow-lg shadow-pink-500/30"
            data-testid="login-submit-button"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>

          <p className="text-center text-gray-400 text-sm mt-6 font-inter">
            Don't have an account?{' '}
            <button type="button" onClick={() => navigate('/signup')} className="text-amber-400 hover:text-amber-300 font-semibold" data-testid="go-to-signup-link">
              Join now
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
